/**
 * React hook for DAG storage operations
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DAGStore, ValidatingDAGStore, DAGObject, ArticleContent, MIN_VALID_TIMESTAMP, validateDAGObject, Identity, createTombstoneContent } from '@amorphous/core';
import { IndexedDBBackend } from '../storage/indexeddb';

export interface CleanupResult {
  total: number;
  valid: number;
  deleted: number;
  errors: Array<{ hash: string; type: string; errors: string[] }>;
}

interface UseDAGResult {
  store: DAGStore;
  articles: DAGObject<ArticleContent>[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  addObject: (obj: DAGObject) => Promise<string>;
  getVoteCounts: (hash: string) => Promise<{ up: number; down: number; total: number }>;
  cleanup: () => Promise<CleanupResult>;
  totalObjects: number;
  /** Delete an object by creating a tombstone (requires identity of creator) */
  deleteObject: (hash: string, identity: Identity, reason?: string) => Promise<string>;
  /** Check if an object has been tombstoned */
  isTombstoned: (hash: string) => boolean;
}

export function useDAG(): UseDAGResult {
  // Keep reference to backend for cleanup operations
  const backend = useMemo(() => new IndexedDBBackend(), []);
  // Use ValidatingDAGStore to reject invalid objects (bad hash, signature, timestamp, pubkey)
  const store = useMemo(() => new ValidatingDAGStore(backend, {
    allowServerSigned: true,  // Accept CGI-created objects
  }), [backend]);
  const [articles, setArticles] = useState<DAGObject<ArticleContent>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalObjects, setTotalObjects] = useState(0);
  const initialLoadDone = useRef(false);

  const refresh = useCallback(async () => {
    try {
      // Only show loading spinner on initial load, not on refreshes
      if (!initialLoadDone.current) {
        setLoading(true);
      }

      // Add timeout to prevent hanging on broken IndexedDB
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('IndexedDB timeout')), 5000)
      );

      // Count total objects for debugging
      let allObjects: DAGObject[] = [];
      try {
        allObjects = await Promise.race([backend.getAll(), timeoutPromise]);
        setTotalObjects(allObjects.length);
        console.log(`[DAG] Refresh: ${allObjects.length} total objects in IndexedDB`);
      } catch (err) {
        console.warn('[DAG] Failed to get all objects:', err);
        setTotalObjects(-1); // Indicate error
      }

      const feed = await Promise.race([store.getArticlesFeed(), timeoutPromise]);
      console.log(`[DAG] getArticlesFeed returned ${feed.length} articles`);

      // Filter out malformed entries (must have creator, hash, type, valid timestamp)
      const validArticles = (feed as DAGObject<ArticleContent>[]).filter(article =>
        article && article.creator && article.hash && article.type &&
        article.timestamp >= MIN_VALID_TIMESTAMP
      );
      console.log(`[DAG] After filtering: ${validArticles.length} valid articles`);
      setArticles(validArticles);
    } catch (err) {
      console.error('[DAG] Refresh error:', err);
      // Don't set error state on timeout - just show empty feed
      if (err instanceof Error && err.message === 'IndexedDB timeout') {
        console.warn('[DAG] IndexedDB timed out, continuing with empty feed');
        setArticles([]);
        setTotalObjects(-1);
      } else {
        setError(err instanceof Error ? err : new Error('Failed to load articles'));
      }
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [store, backend]);

  // Initialize tombstone cache and then refresh
  useEffect(() => {
    const init = async () => {
      try {
        await store.initTombstoneCache();
        console.log('[DAG] Tombstone cache initialized');
      } catch (err) {
        console.warn('[DAG] Failed to init tombstone cache:', err);
      }
      refresh();
    };
    init();
  }, [store, refresh]);

  const addObject = useCallback(async (obj: DAGObject) => {
    const hash = await store.put(obj);
    await refresh();
    return hash;
  }, [store, refresh]);

  const getVoteCounts = useCallback(async (hash: string) => {
    return store.getVoteCounts(hash);
  }, [store]);

  /**
   * Delete an object by creating a tombstone
   * Only the creator of the object can delete it
   */
  const deleteObject = useCallback(async (
    hash: string,
    identity: Identity,
    reason?: string
  ): Promise<string> => {
    // Verify the object exists and we're the creator
    const obj = await store.get(hash);
    if (!obj) {
      throw new Error('Object not found');
    }
    if (obj.creator !== identity.publicKey) {
      throw new Error('You can only delete your own content');
    }

    // Create tombstone
    const { type, content } = createTombstoneContent(hash, reason);
    const tombstone = identity.createObject(type, content);

    // Store and broadcast
    const tombstoneHash = await store.put(tombstone);
    console.log(`[DAG] Created tombstone ${tombstoneHash} for ${hash}`);

    // Refresh to update the feed
    await refresh();
    return tombstoneHash;
  }, [store, refresh]);

  /**
   * Check if an object has been tombstoned (deleted)
   */
  const isTombstoned = useCallback((hash: string): boolean => {
    return store.isTombstoned(hash);
  }, [store]);

  /**
   * Scan all objects in IndexedDB and delete invalid ones
   * Returns a report of what was cleaned up
   */
  const cleanup = useCallback(async (): Promise<CleanupResult> => {
    const result: CleanupResult = {
      total: 0,
      valid: 0,
      deleted: 0,
      errors: [],
    };

    const allObjects = await backend.getAll();
    result.total = allObjects.length;

    for (const obj of allObjects) {
      const validation = validateDAGObject(obj, { allowServerSigned: true });

      if (validation.valid) {
        result.valid++;
      } else {
        // Delete invalid object
        await backend.delete(obj.hash);
        result.deleted++;
        result.errors.push({
          hash: obj.hash,
          type: obj.type || 'unknown',
          errors: validation.errors,
        });
        console.log(`[Cleanup] Deleted invalid object ${obj.hash?.slice(0, 8)}: ${validation.errors.join(', ')}`);
      }
    }

    // Refresh the feed after cleanup
    await refresh();

    console.log(`[Cleanup] Complete: ${result.total} total, ${result.valid} valid, ${result.deleted} deleted`);
    return result;
  }, [backend, refresh]);

  return { store, articles, loading, error, refresh, addObject, getVoteCounts, cleanup, totalObjects, deleteObject, isTombstoned };
}
